import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useSettings } from '../../contexts/SettingsContext';
import AppHeader from '../../components/AppHeader';
import ResumeProgressBar from '../../components/ResumeProgressBar';
import ResumeUploadLog from '../../components/ResumeUploadLog';
import Toast from '../../components/Toast';
import DottedCircleLoader from '../../components/DottedCircleLoader';
import FilterDropdown from '../../components/FilterDropdown';
import { DonutChart, HorizontalBarChart, StatCard } from '../../components/charts';
import { getMetadata, getUserId, getCid, hasValidCid } from '../../utils/encryptedStorage';
import coreApi from '../../utils/coreApi';
import { getFeatureBlockedMessage, useCompanyFeatureAccess } from '../../utils/companyFeatureAccess';

// ── Upload config ──
const UPLOAD_CONFIG = {
  SOFT_FILE_LIMIT: 50,          // Recommended max — shown to user
  HARD_FILE_LIMIT: 60,          // Absolute max (50 + 10 grace) — blocked above this
  MAX_FILE_SIZE_MB: 10,         // Max size per file in MB
  MAX_TOTAL_SIZE_MB: 100,       // Max total size of all selected files
  CHUNK_SIZE: 2,               // Files per chunk sent to backend
  ACCEPTED_TYPES: {
    'application/pdf': '.pdf',
    'application/msword': '.doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  },
  ACCEPTED_EXTENSIONS: ['.pdf', '.doc', '.docx'],
};

/** Check if a file has an accepted resume type */
const isAcceptedResumeFile = (file) => {
  // Check MIME type first
  if (UPLOAD_CONFIG.ACCEPTED_TYPES[file.type]) return true;
  // Fallback: check extension (some browsers don't set MIME for .doc)
  const ext = '.' + file.name.split('.').pop().toLowerCase();
  return UPLOAD_CONFIG.ACCEPTED_EXTENSIONS.includes(ext);
};

/**
 * ResumeParsingPage
 * Main page for resume upload, database browsing, and analytics
 * Migrated from old_eeezo_ux ResumeData + ResumeBox with TalentGPT theming
 *
 * Flow: UX -> Core -> AI Automation
 */
const ResumeParsingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  const { settings } = useSettings();
  const { canUseFeature, loading: featureAccessLoading } = useCompanyFeatureAccess();
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  // View mode: 'upload', 'analytics', or 'database'
  const [viewMode, setViewMode] = useState('upload');
  const [cameFromAnalytics, setCameFromAnalytics] = useState(false); // Track drill-down navigation
  const [isSearching, setIsSearching] = useState(false); // Debounce loading indicator

  // Resume preview modal
  const [previewResume, setPreviewResume] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // PDF viewer modal state
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [isPdfLoading, setIsPdfLoading] = useState(false);

  // Duplicate decision modal
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [exactDuplicates, setExactDuplicates] = useState([]);
  const [duplicateSelections, setDuplicateSelections] = useState({});
  const [isProcessingDuplicates, setIsProcessingDuplicates] = useState(false);

  // Parsing stats modal
  const [parsingStats, setParsingStats] = useState(null);
  const [showStatsModal, setShowStatsModal] = useState(false);

  // Theme colors - centralized
  const primaryColor = settings.theme?.colors?.primary || '#3B5BDB';
  const secondaryColor = settings.theme?.colors?.secondary || '#28ACA8';
  const accentColor = settings.theme?.colors?.accent || '#28ACA8';

  // State management
  const [isUploading, setIsUploading] = useState(false);
  const [resumeFiles, setResumeFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [haveData, setHaveData] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [progressTrigger, setProgressTrigger] = useState(0);
  const [uploadFileLogs, setUploadFileLogs] = useState([]);
  const [isParsingActive, setIsParsingActive] = useState(false);
  const [resumeList, setResumeList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Toast notification state
  const [toast, setToast] = useState({ message: '', type: 'info', visible: false });

  // AI automation health state
  const [aiHealthy, setAiHealthy] = useState(true);
  const [healthChecked, setHealthChecked] = useState(false);

  // Filter state
  const [filterExperience, setFilterExperience] = useState('all');
  const [filterIndustry, setFilterIndustry] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  // Analytics stats
  const [analyticsStats, setAnalyticsStats] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  // Active chart type driven by clicking StatCards
  const [activeChartType, setActiveChartType] = useState('role');

  // Animation key for charts - increments to trigger re-mount animation
  const [chartAnimKey, setChartAnimKey] = useState(0);
  const [showAllCategories, setShowAllCategories] = useState(false);

  const [resumeStats, setResumeStats] = useState({
    total: 0,
    byRole: [],
    byExperience: [],
    byIndustry: [],
    byLocation: []
  });

  // Pagination state (server-driven)
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Debounce timer ref for search
  const searchTimerRef = useRef(null);
  const skipNextDebounceRef = useRef(false);

  // User metadata
  const [userData, setUserData] = useState({
    userId: null,
    cid: null
  });
  const openedFromDashboard = useMemo(() => new URLSearchParams(location.search).get('from_dashboard') === 'true', [location.search]);
  const handleBackNavigation = useCallback(() => {
    if (openedFromDashboard) {
      navigate('/dashboard');
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/dashboard');
  }, [navigate, openedFromDashboard]);

  // Sanitize technical error messages into user-friendly text
  const sanitizeErrorMessage = (message) => {
    if (!message) return 'Something went wrong. Please try again.';
    const msg = message.toString();
    if (msg.includes('ECONNREFUSED') || msg.includes('NetworkError') || msg.includes('Failed to fetch')) {
      return 'Unable to reach the resume parsing service.';
    }
    if (msg.includes('timeout') || msg.includes('ETIMEDOUT')) {
      return 'The request timed out. The server may be busy — please try again in a moment.';
    }
    if (msg.includes('401') || msg.includes('Unauthorized')) {
      return 'Your session has expired. Please log in again.';
    }
    if (msg.includes('413') || msg.includes('too large')) {
      return 'The file(s) are too large. Please reduce the file size and try again.';
    }
    return msg;
  };

  // Show toast notification
  const showNotification = useCallback((message, type = 'info') => {
    const displayMessage = type === 'error' ? sanitizeErrorMessage(message) : message;
    setToast({ message: displayMessage, type, visible: true });
  }, []);

  const closeToast = useCallback(() => {
    setToast(prev => ({ ...prev, visible: false }));
  }, []);

  // Load user metadata from encrypted localStorage
  useEffect(() => {
    const loadUserData = () => {
      try {
        const metadata = getMetadata();
        if (metadata) {
          setUserData({
            userId: metadata.uid || metadata.userId || metadata.id,
            cid: metadata.cid
          });
          if (!metadata.cid) {
            showNotification('Please set up your company profile to use all features.', 'warning');
          }
        } else {
          showNotification('Please log in to continue.', 'error');
          setTimeout(() => navigate('/auth/login'), 2000);
        }
      } catch (error) {
        console.error('Failed to load user metadata:', error);
        setUserData({ userId: '1', cid: '1' });
      }
    };
    loadUserData();
  }, []);

  // AI quota warning state
  const [aiQuotaExceeded, setAiQuotaExceeded] = useState(false);

  // Check AI automation health + OpenAI quota on page load
  useEffect(() => {
    const checkAiHealth = async () => {
      try {
        const result = await coreApi.get('/resume-parsing/health');
        if (result.ai_automation_status === 'unreachable') {
          setAiHealthy(false);
        } else {
          setAiHealthy(true);
        }
      } catch (error) {
        console.error('AI Automation health check failed:', error);
        setAiHealthy(false);
      } finally {
        setHealthChecked(true);
      }

      // Check AI quota — same endpoint the header badge uses
      try {
        const quotaResult = await coreApi.get('/openai-status');
        console.log('[ResumeParsingPage] Quota check result:', quotaResult);
        const quotaData = quotaResult?.data || quotaResult;
        const isQuotaError = quotaData?.error_code === 'quota_exceeded'
          || quotaData?.status === 'error'
          || (quotaData?.message || '').toLowerCase().includes('quota');
        setAiQuotaExceeded(isQuotaError);
      } catch (_) {
        // If check fails, don't block uploads — assume quota is fine
      }
    };
    checkAiHealth();
  }, []);

  // Fetch resume list from API with server-side pagination and search
  const fetchResumeList = useCallback(async (page = 1, search = '') => {
    if (!userData.cid) return;
    setIsLoading(true);
    try {
      const offset = (page - 1) * itemsPerPage;
      const result = await coreApi.post('/resume-parsing/list', {
        cid: userData.cid,
        user_id: userData.userId,
        type: 'fordashboard',
        offset,
        limit: itemsPerPage,
        search: search.trim()
      });

      if (result.data) {
        const resumes = Array.isArray(result.data) ? result.data : result.data.resumes || [];
        const serverTotal = result.data.total_count ?? resumes.length;
        setResumeList(resumes);
        setTotalCount(serverTotal);
        setHaveData(serverTotal > 0);
      } else {
        setResumeList([]);
        setTotalCount(0);
        setHaveData(false);
      }
    } catch (error) {
      console.error('Error fetching resume list:', error);
      setResumeList([]);
      setTotalCount(0);
      setHaveData(false);
      showNotification(
        error.message || 'Unable to load your resume database. Please check your connection and try again.',
        'error'
      );
    } finally {
      setIsLoading(false);
      setInitialLoadComplete(true);
    }
  }, [userData.cid, userData.userId, itemsPerPage, showNotification]);

  // Fetch analytics stats (SQL-based aggregation — includes role & industry)
  const fetchAnalyticsStats = useCallback(async () => {
    if (!userData.cid) return;
    setAnalyticsLoading(true);
    try {
      const result = await coreApi.post('/resume-parsing/stats', { cid: userData.cid });

      if (result.data) {
        setAnalyticsStats(result.data);
      }
    } catch (error) {
      console.error('Error fetching analytics stats:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [userData.cid]);

  // Fetch resume list on mount and when refresh triggered (e.g. after parsing completes)
  useEffect(() => {
    if (userData.cid) {
      fetchResumeList(currentPage, searchQuery);
    } else if (userData.userId !== null) {
      setInitialLoadComplete(true);
    }
  }, [userData.cid, refreshTrigger]);

  // Fetch analytics stats on mount (so stat cards have data immediately)
  useEffect(() => {
    if (userData.cid) {
      fetchAnalyticsStats();
    }
  }, [userData.cid, fetchAnalyticsStats]);

  // Unique experience levels for filter dropdown
  const uniqueExperienceLevels = useMemo(() => {
    const levels = new Set();
    resumeList.forEach(r => {
      if (r.experience_level) levels.add(r.experience_level.trim());
    });
    return Array.from(levels).sort();
  }, [resumeList]);

  // Unique industries for filter dropdown
  const uniqueIndustries = useMemo(() => {
    const industries = new Set();
    resumeList.forEach(r => {
      if (r.industry_backgrounds) {
        r.industry_backgrounds.split(',').map(i => i.trim()).filter(Boolean).forEach(ind => industries.add(ind));
      }
    });
    return Array.from(industries).sort();
  }, [resumeList]);

  // Unique locations for filter dropdown
  const uniqueLocations = useMemo(() => {
    const locations = new Set();
    let hasEmpty = false;
    resumeList.forEach(r => {
      const loc = (r.location || '').trim();
      if (loc) {
        locations.add(loc);
      } else {
        hasEmpty = true;
      }
    });
    const sorted = Array.from(locations).sort();
    if (hasEmpty) sorted.push('Others');
    return sorted;
  }, [resumeList]);

  // Sort resumes within the current page (sorting is client-side within page)
  const sortedResumes = useMemo(() => {
    const sorted = [...resumeList];
    sorted.sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'name':
          aVal = (a.name || '').toLowerCase();
          bVal = (b.name || '').toLowerCase();
          break;
        case 'experience':
          aVal = Number(a.total_experience_in_months) || 0;
          bVal = Number(b.total_experience_in_months) || 0;
          break;
        case 'role':
          aVal = (a.potential_roles || '').toLowerCase();
          bVal = (b.potential_roles || '').toLowerCase();
          break;
        case 'date':
          aVal = new Date(a.created_at || 0).getTime();
          bVal = new Date(b.created_at || 0).getTime();
          break;
        default:
          aVal = (a[sortField] || '').toString().toLowerCase();
          bVal = (b[sortField] || '').toString().toLowerCase();
      }
      if (typeof aVal === 'string') {
        const cmp = aVal.localeCompare(bVal);
        return sortDirection === 'asc' ? cmp : -cmp;
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return sorted;
  }, [resumeList, sortField, sortDirection]);

  // Server-side pagination — totalCount comes from backend
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const paginatedResumes = sortedResumes; // already paginated by server

  // Re-fetch when page changes
  useEffect(() => {
    if (userData.cid && initialLoadComplete) {
      fetchResumeList(currentPage, searchQuery);
    }
  }, [currentPage]);

  // Debounced search — re-fetch from page 1 on search change (keyboard typing only)
  useEffect(() => {
    if (!userData.cid || !initialLoadComplete) return;
    // Skip if this change was triggered by a category click (already fetched immediately)
    if (skipNextDebounceRef.current) {
      skipNextDebounceRef.current = false;
      return;
    }
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchResumeList(1, searchQuery).finally(() => setIsSearching(false));
    }, 400);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [searchQuery]);

  const goToPage = (page) => setCurrentPage(Math.max(1, Math.min(page, totalPages)));

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400 dark:text-gray-600 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 ml-1" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
      </svg>
    ) : (
      <svg className="w-4 h-4 ml-1" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
      </svg>
    );
  };

  // Clear filters
  const clearAllFilters = () => {
    setSearchQuery('');
    setFilterExperience('all');
    setFilterIndustry('all');
    setFilterLocation('all');
    setSortField('name');
    setSortDirection('asc');
    setCurrentPage(1);
    setCameFromAnalytics(false);
  };

  // Reset pagination when filters change
  useEffect(() => {
    if (initialLoadComplete && userData.cid) {
      setCurrentPage(1);
      fetchResumeList(1, searchQuery);
    }
  }, [filterExperience, filterIndustry, filterLocation]);

  const hasActiveFilters = searchQuery.trim().length > 0 || filterExperience !== 'all' || filterIndustry !== 'all' || filterLocation !== 'all' || sortField !== 'name' || sortDirection !== 'asc';

  // Validate and add files with soft limit + type + size checks
  const validateAndAddFiles = useCallback((selectedFiles) => {
    const accepted = [];
    const rejected = [];
    const oversized = [];

    for (const file of selectedFiles) {
      if (!isAcceptedResumeFile(file)) {
        rejected.push(file.name);
      } else if (file.size > UPLOAD_CONFIG.MAX_FILE_SIZE_MB * 1024 * 1024) {
        oversized.push(file.name);
      } else {
        accepted.push(file);
      }
    }

    if (rejected.length > 0) {
      showNotification(
        `${rejected.length} file(s) excluded — only PDF, DOC, and DOCX files are supported.`,
        'warning'
      );
    }
    if (oversized.length > 0) {
      showNotification(
        `${oversized.length} file(s) exceed the ${UPLOAD_CONFIG.MAX_FILE_SIZE_MB}MB size limit and were excluded.`,
        'warning'
      );
    }

    if (accepted.length === 0) return;

    setResumeFiles(prev => {
      const combined = [...prev, ...accepted];

      // Hard block — reject entirely above grace limit
      if (combined.length > UPLOAD_CONFIG.HARD_FILE_LIMIT) {
        showNotification(
          `You've selected ${combined.length} files which exceeds the maximum of ${UPLOAD_CONFIG.SOFT_FILE_LIMIT}. Please remove some files and try again.`,
          'error'
        );
        return prev;
      }

      // Total size check
      const totalSizeMB = combined.reduce((sum, f) => sum + f.size, 0) / (1024 * 1024);
      if (totalSizeMB > UPLOAD_CONFIG.MAX_TOTAL_SIZE_MB) {
        showNotification(
          `Total file size (${Math.round(totalSizeMB)}MB) exceeds the ${UPLOAD_CONFIG.MAX_TOTAL_SIZE_MB}MB limit. Please remove some files.`,
          'error'
        );
        return prev;
      }

      // Grace range (51–60) — allow but recommend reducing next time
      if (combined.length > UPLOAD_CONFIG.SOFT_FILE_LIMIT) {
        showNotification(
          `You've selected ${combined.length} files. We recommend ${UPLOAD_CONFIG.SOFT_FILE_LIMIT} or fewer for optimal performance. Consider uploading in smaller batches next time.`,
          'warning'
        );
      }
      return combined;
    });
  }, [showNotification]);

  // Handle file selection — reset value so re-selecting same files works
  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    e.target.value = ''; // Reset so same file can be selected again
    if (files.length > 0) validateAndAddFiles(files);
  };

  // Handle folder selection — extracts all resume files recursively
  const handleFolderInputChange = (e) => {
    const allFiles = Array.from(e.target.files);
    e.target.value = ''; // Reset
    // webkitdirectory returns ALL files in the folder tree — filter to resumes only
    const resumeFiles = allFiles.filter(isAcceptedResumeFile);
    if (allFiles.length > 0 && resumeFiles.length === 0) {
      showNotification('No resume files (PDF, DOC, DOCX) found in the selected folder.', 'warning');
    } else if (resumeFiles.length < allFiles.length) {
      showNotification(`Found ${resumeFiles.length} resume file(s) out of ${allFiles.length} total files in the folder.`, 'info');
    }
    if (resumeFiles.length > 0) {
      validateAndAddFiles(resumeFiles);
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Handle both files and folders from drag-drop
    const items = e.dataTransfer.items;
    if (items && items.length > 0) {
      const filePromises = [];
      for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry?.();
        if (entry) {
          filePromises.push(_readEntry(entry));
        }
      }
      if (filePromises.length > 0) {
        Promise.all(filePromises).then((results) => {
          const allFiles = results.flat();
          if (allFiles.length > 0) validateAndAddFiles(allFiles);
        });
        return;
      }
    }
    // Fallback: plain file drop
    validateAndAddFiles(Array.from(e.dataTransfer.files));
  };

  // Recursively read files from a dropped folder entry
  const _readEntry = (entry) => {
    return new Promise((resolve) => {
      if (entry.isFile) {
        entry.file((file) => resolve(isAcceptedResumeFile(file) ? [file] : []), () => resolve([]));
      } else if (entry.isDirectory) {
        const reader = entry.createReader();
        reader.readEntries((entries) => {
          Promise.all(entries.map(_readEntry)).then((results) => resolve(results.flat()));
        }, () => resolve([]));
      } else {
        resolve([]);
      }
    });
  };

  const removeFile = (index) => setResumeFiles(prev => prev.filter((_, i) => i !== index));

  // Upload state for chunked uploads
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, phase: '' });

  // Upload resumes in chunks
  const handleUpload = async () => {
    if (!canUseFeature('resume_upload')) {
      showNotification(getFeatureBlockedMessage('resume_upload'), 'warning');
      return;
    }
    if (resumeFiles.length === 0) {
      showNotification('Please select at least one resume file before uploading.', 'warning');
      return;
    }
    if (!userData.userId) {
      showNotification('Your session has expired. Please log in again to continue.', 'error');
      return;
    }
    if (!userData.cid) {
      showNotification('Company profile is required. Redirecting you to complete the setup...', 'warning');
      setTimeout(() => navigate('/auth/workspace-setup'), 2000);
      return;
    }
    if (!aiHealthy) {
      showNotification('Resume parsing service is currently unavailable. Please try again later.', 'error');
      return;
    }
    if (aiQuotaExceeded) {
      showNotification('Cannot upload resumes — AI service quota exceeded. Please check your AI provider billing or wait for the quota to reset.', 'error');
      return;
    }

    const totalFiles = resumeFiles.length;
    const chunkSize = UPLOAD_CONFIG.CHUNK_SIZE;
    const totalChunks = Math.ceil(totalFiles / chunkSize);
    const uploadSessionId = `resume_upload_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

    setIsUploading(true);
    setUploadProgress({ current: 0, total: totalFiles, phase: 'uploading' });
    statsShownRef.current = false;
    // Only clear logs if not currently parsing (uploading more while processing = same session)
    if (!isParsingActive) {
      setUploadFileLogs([]);
      // Server-side logs are automatically replaced by UPSERT when new batch starts
    }

    let uploadedCount = 0;
    let failedCount = 0;
    let allExactDuplicates = [];
    let hasParsingStarted = false;

    try {
      for (let i = 0; i < totalChunks; i++) {
        const chunkFiles = resumeFiles.slice(i * chunkSize, (i + 1) * chunkSize);

        const formData = new FormData();
        chunkFiles.forEach(file => formData.append('file', file));
        formData.append('userId', userData.userId);
        formData.append('cid', userData.cid);
        formData.append('chunk_index', i.toString());
        formData.append('total_chunks', totalChunks.toString());
        formData.append('total_files', totalFiles.toString());
        formData.append('upload_session_id', uploadSessionId);

        try {
          const result = await coreApi.upload('/resume-parsing/upload', formData);

          uploadedCount += chunkFiles.length;
          setUploadProgress({ current: uploadedCount, total: totalFiles, phase: 'uploading' });

          // Collect exact duplicates from hash check
          if (result.data?.exact_duplicates?.length > 0) {
            allExactDuplicates = [...allExactDuplicates, ...result.data.exact_duplicates];
          }

          if (result.data?.status !== 'upload_only') {
            hasParsingStarted = true;
          }
        } catch (chunkError) {
          console.error(`Chunk ${i + 1}/${totalChunks} failed:`, chunkError);
          failedCount += chunkFiles.length;
        }
      }

      // Summary notification — tailor message based on what actually happened
      const newCount = uploadedCount - allExactDuplicates.length;
      if (failedCount > 0) {
        showNotification(
          `${uploadedCount} of ${totalFiles} file(s) uploaded. ${failedCount} failed — please retry those files.`,
          'warning'
        );
      } else if (allExactDuplicates.length > 0 && newCount === 0) {
        // All files were exact duplicates — no parsing started
        // Don't show "parsing started" toast — the duplicate modal handles it
      } else if (!hasParsingStarted) {
        showNotification(
          'Files uploaded but the resume parsing service is currently unavailable. Parsing will retry automatically when the service is back.',
          'warning'
        );
      } else if (allExactDuplicates.length > 0) {
        showNotification(
          `${newCount} new resume(s) processing. ${allExactDuplicates.length} duplicate(s) need your review.`,
          'success'
        );
      } else {
        showNotification(`${newCount} resume(s) uploaded! Parsing in progress — results will appear in Analytics & Database once ready.`, 'success');
      }

      setResumeFiles([]);
      setUploadProgress({ current: 0, total: 0, phase: '' });

      // Trigger progress tracking for parsing
      if (hasParsingStarted) {
        setProgressTrigger(prev => prev + 1);
      }

      // Show duplicate decision modal if exact duplicates were found
      if (allExactDuplicates.length > 0) {
        setExactDuplicates(allExactDuplicates);
        // Default: all checked (reparse)
        const selections = {};
        allExactDuplicates.forEach((_, i) => { selections[i] = true; });
        setDuplicateSelections(selections);
        setShowDuplicateModal(true);
      }

    } catch (error) {
      console.error('Upload error:', error);
      showNotification(error.message || 'Failed to upload resumes. Please check your connection and try again.', 'error');
    } finally {
      setIsUploading(false);
      setUploadProgress({ current: 0, total: 0, phase: '' });
    }
  };

  // Retry failed resumes
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const handleRetryFailed = async () => {
    if (!userData.cid) return;
    setIsRetrying(true);
    try {
      const result = await coreApi.post('/resume-parsing/retry-failed', {
        cid: userData.cid,
        user_id: userData.userId,
      });
      if (result.data?.status === 'retrying') {
        setRetryCount(result.data.count || 0);
        showNotification(`Retrying ${result.data.count} resume(s)...`, 'info');
        setProgressTrigger(prev => prev + 1);
      } else if (result.data?.status === 'no_pending') {
        showNotification('No resumes pending retry.', 'info');
      } else {
        showNotification(result.data?.error || 'Retry request failed.', 'error');
      }
    } catch (error) {
      if (error.status === 429) {
        showNotification('AI service is still rate-limited. Please try again in a few minutes.', 'warning');
      } else {
        showNotification(error.message || 'Failed to retry.', 'error');
      }
    } finally {
      setIsRetrying(false);
    }
  };

  // Duplicate modal handlers
  const toggleDuplicateSelection = (index) => {
    setDuplicateSelections(prev => ({ ...prev, [index]: !prev[index] }));
  };
  const toggleAllDuplicates = () => {
    const allSelected = Object.values(duplicateSelections).every(Boolean);
    const next = {};
    exactDuplicates.forEach((_, i) => { next[i] = !allSelected; });
    setDuplicateSelections(next);
  };

  const handleDuplicateDecision = async (action) => {
    // action: 'skip' or 'reparse'
    const selectedCount = Object.values(duplicateSelections).filter(Boolean).length;
    const totalCount = exactDuplicates.length;

    if (action === 'skip') {
      // Skip doesn't need a backend call — just close the modal
      const skippedCount = selectedCount || totalCount;
      showNotification(
        `${skippedCount} duplicate resume(s) skipped — existing records kept as-is.`,
        'info'
      );
      setShowDuplicateModal(false);
      setExactDuplicates([]);
      return;
    }

    // Reparse: send selected duplicates to backend for re-processing
    setIsProcessingDuplicates(true);
    try {
      const duplicates = exactDuplicates
        .map((dup, i) => ({
          existing_doc_id: dup.existing_doc_id,
          file_hash: dup.file_hash,
          action: duplicateSelections[i] ? 'reparse' : 'skip',
        }));

      const result = await coreApi.post('/resume-parsing/process-duplicates', {
        cid: userData.cid,
        userId: userData.userId,
        duplicates,
      });

      const data = result.data;
      const reparseCount = data?.reparse_count || 0;
      const skippedCount = data?.skipped_count || 0;
      const failedForwardCount = data?.failed_forward_count || 0;

      if (failedForwardCount > 0 && reparseCount > 0) {
        showNotification(`${reparseCount} duplicate resume(s) sent for re-parsing. ${failedForwardCount} could not be started and can be retried.`, 'warning');
      } else if (failedForwardCount > 0) {
        showNotification(`${failedForwardCount} duplicate resume(s) could not be sent for re-parsing. Please try again.`, 'error');
      } else if (reparseCount > 0 && skippedCount > 0) {
        showNotification(`${reparseCount} resume(s) sent for re-parsing, ${skippedCount} skipped.`, 'success');
      } else if (reparseCount > 0) {
        showNotification(`${reparseCount} duplicate resume(s) sent for re-parsing. Results will appear shortly.`, 'success');
      } else {
        showNotification(`${skippedCount} duplicate resume(s) skipped — existing records kept as-is.`, 'info');
      }

      if (reparseCount > 0) {
        setProgressTrigger(prev => prev + 1);
      }
    } catch (error) {
      console.error('Process duplicates error:', error);
      showNotification('Something went wrong while processing duplicates. Please try uploading again.', 'error');
    } finally {
      setIsProcessingDuplicates(false);
      setShowDuplicateModal(false);
      setExactDuplicates([]);
    }
  };

  // Track queued retry count + file logs from SSE progress updates
  const handleProgressUpdate = useCallback((progressData) => {
    const queued = progressData?.queued_retry || progressData?.progress?.queued_retry || 0;
    if (queued > 0) {
      setRetryCount(queued);
    }
    // Track per-file logs from SSE — replace entirely (SSE sends full list each time)
    const fileLogs = progressData?.file_logs || progressData?.progress?.file_logs;
    if (fileLogs) {
      setUploadFileLogs(fileLogs);
    }
    const status = progressData?.status || progressData?.progress?.status;
    setIsParsingActive(status === 'processing');
  }, []);

  // Handle progress completion - ref-guarded to prevent duplicate modal
  const statsShownRef = useRef(false);
  const handleProgressComplete = useCallback((stats) => {
    console.log('Processing complete with stats:', stats);
    if (statsShownRef.current) {
      console.log('Stats modal already shown for this session — skipping');
      return;
    }
    setIsParsingActive(false);
    // Show final file logs briefly — they'll be visible until next upload clears them
    const finalLogs = stats?.file_logs || stats?.progress?.file_logs;
    if (finalLogs && finalLogs.length > 0) {
      setUploadFileLogs(finalLogs);
    }
    setRefreshTrigger(prev => prev + 1);
    // Re-fetch analytics with fresh data
    fetchAnalyticsStats();
    if (stats && stats.progress) {
      statsShownRef.current = true;
      setParsingStats(stats);
      setShowStatsModal(true);
      // Check if there are queued retries to show the retry button
      const queued = stats.queued_retry || stats.progress?.queued_retry || 0;
      if (queued > 0) {
        setRetryCount(queued);
        showNotification(
          `${stats.progress.completed} resume(s) completed. ${queued} queued for retry due to API limits.`,
          'warning'
        );
      }
    }
  }, [fetchAnalyticsStats, showNotification]);

  const closeStatsModal = () => {
    setShowStatsModal(false);
    setParsingStats(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatExperience = (months) => {
    if (!months) return '-';
    const m = Number(months);
    if (isNaN(m)) return '-';
    const years = Math.floor(m / 12);
    const rem = m % 12;
    if (years === 0) return `${rem}mo`;
    if (rem === 0) return `${years}y`;
    return `${years}y ${rem}mo`;
  };

  // Compute average experience for stat card
  const avgExperience = useMemo(() => {
    if (analyticsStats?.avgExperienceMonths) return formatExperience(analyticsStats.avgExperienceMonths);
    if (resumeList.length === 0) return '-';
    const withExp = resumeList.filter(r => r.total_experience_in_months);
    if (withExp.length === 0) return '-';
    const avg = Math.round(withExp.reduce((sum, r) => sum + (Number(r.total_experience_in_months) || 0), 0) / withExp.length);
    return formatExperience(avg);
  }, [analyticsStats, resumeList]);

  // Resume preview
  const openResumePreview = (resume) => { setPreviewResume(resume); setIsPreviewOpen(true); };
  const closeResumePreview = () => { setIsPreviewOpen(false); setPreviewResume(null); };
  const closePdfViewer = () => {
    setPdfViewerOpen(false);
    if (pdfBlobUrl) { URL.revokeObjectURL(pdfBlobUrl); setPdfBlobUrl(null); }
  };

  // Escape key closes all modals
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key !== 'Escape') return;
      if (pdfViewerOpen) { closePdfViewer(); return; }
      if (isPreviewOpen) { closeResumePreview(); return; }
      if (showDuplicateModal) { setShowDuplicateModal(false); return; }
      if (showStatsModal) { closeStatsModal(); return; }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [pdfViewerOpen, isPreviewOpen, showDuplicateModal, showStatsModal]);

  const viewOriginalResume = async (docId) => {
    if (!docId) { showNotification('Resume file is not available for this candidate.', 'warning'); return; }
    setIsPdfLoading(true);
    try {
      const { response } = await coreApi.get(`/resume-parsing/file/${docId}?cid=${userData.cid}`, { skipDecryption: true });
      if (!response.ok) {
        throw new Error('Failed to fetch resume file');
      }
      const blob = await response.blob();
      setPdfBlobUrl(URL.createObjectURL(blob));
      setPdfViewerOpen(true);
    } catch (error) {
      console.error('Error fetching resume file:', error);
      showNotification('Unable to load the resume document. The file may have been moved or deleted.', 'error');
    } finally {
      setIsPdfLoading(false);
    }
  };

  // Handle stat card click -> switch chart type
  const handleStatCardClick = (type) => {
    setActiveChartType(type);
    setChartAnimKey(prev => prev + 1);
    setShowAllCategories(false);
  };

  // ─────────────────────────────── RENDER ───────────────────────────────

  if (featureAccessLoading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-gray-100 dark:bg-gray-950">
        <DottedCircleLoader />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-950 transition-[background-color] duration-250 w-full flex flex-col">
      <AppHeader />

      {/* Toast Notification - bottom right, professional style */}
      <Toast
        message={toast.message}
        type={toast.type}
        visible={toast.visible}
        onClose={closeToast}
        duration={4000}
      />

      {/* ─────── Resume Preview Modal ─────── */}
      {isPreviewOpen && previewResume && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={closeResumePreview}>
          <div
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden m-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white text-lg font-semibold"
                  style={{ background: `linear-gradient(135deg, ${primaryColor}, ${accentColor})` }}
                >
                  {(previewResume.name || 'C').charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{previewResume.name || 'Candidate'}</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{previewResume.email || 'No email'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => viewOriginalResume(previewResume.doc_id)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                  style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
                  title="View Original Resume"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  View Resume
                </button>
                <button
                  onClick={closeResumePreview}
                  className="p-2 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Contact Info */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Contact Information</h3>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span className="text-gray-700 dark:text-gray-300">{previewResume.email || '-'}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <span className="text-gray-700 dark:text-gray-300">{previewResume.phone || '-'}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-gray-700 dark:text-gray-300">{previewResume.location || '-'}</span>
                    </div>
                  </div>
                </div>

                {/* Professional Info */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Professional Info</h3>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 space-y-3">
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Experience Level</span>
                      <p className="text-gray-700 dark:text-gray-300 font-medium">{previewResume.experience_level || '-'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Total Experience</span>
                      <p className="text-gray-700 dark:text-gray-300 font-medium">{formatExperience(previewResume.total_experience_in_months)}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Potential Roles</span>
                      <p className="text-gray-700 dark:text-gray-300">{previewResume.potential_roles || '-'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Industry Background</span>
                      <p className="text-gray-700 dark:text-gray-300">{previewResume.industry_backgrounds || '-'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Companies</span>
                      <p className="text-gray-700 dark:text-gray-300">{previewResume.companies_worked_at || '-'}</p>
                    </div>
                  </div>
                </div>

                {/* Skills */}
                {previewResume.core_skills && (
                  <div className="md:col-span-2 space-y-4">
                    <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Skills</h3>
                    <div className="flex flex-wrap gap-2">
                      {previewResume.core_skills.split(',').map((skill, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 rounded-full text-sm font-medium"
                          style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
                        >
                          {skill.trim()}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Summary */}
                {previewResume.summary && (
                  <div className="md:col-span-2 space-y-4">
                    <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Summary</h3>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{previewResume.summary}</p>
                  </div>
                )}

                {/* Education */}
                {previewResume.education && (
                  <div className="md:col-span-2 space-y-4">
                    <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Education</h3>
                    <p className="text-gray-700 dark:text-gray-300">{previewResume.education}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─────── Duplicate Decision Modal ─────── */}
      {showDuplicateModal && exactDuplicates.length > 0 && (() => {
        const selectedCount = Object.values(duplicateSelections).filter(Boolean).length;
        const totalCount = exactDuplicates.length;
        return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowDuplicateModal(false)}>
          <div
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden m-4 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center bg-amber-100 dark:bg-amber-900/30">
                    <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Duplicates Found</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {totalCount} file{totalCount !== 1 ? 's' : ''} already exist in your database
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDuplicateModal(false)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Select All + Count */}
            <div className="px-6 py-2.5 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between flex-shrink-0">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600 dark:text-gray-400">
                <input
                  type="checkbox"
                  checked={selectedCount === totalCount}
                  ref={(el) => { if (el) el.indeterminate = selectedCount > 0 && selectedCount < totalCount; }}
                  onChange={toggleAllDuplicates}
                  className="rounded border-gray-300 dark:border-gray-600"
                />
                Select All
              </label>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {selectedCount} of {totalCount} selected
              </span>
            </div>

            {/* File List — scrollable */}
            <div className="px-6 py-3 space-y-2 overflow-y-auto flex-1 min-h-0">
              {exactDuplicates.map((dup, index) => (
                <div key={index} className={`flex items-start gap-3 p-3 rounded-xl transition-colors ${duplicateSelections[index] ? 'bg-amber-50 dark:bg-amber-900/10 ring-1 ring-amber-200 dark:ring-amber-800/40' : 'bg-gray-50 dark:bg-gray-800/50'}`}>
                  <input
                    type="checkbox"
                    checked={!!duplicateSelections[index]}
                    onChange={() => toggleDuplicateSelection(index)}
                    className="mt-1 rounded border-gray-300 dark:border-gray-600 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">{dup.uploaded_file}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-500 truncate">
                      Matches: {dup.existing_name || 'Unknown'}{dup.existing_email ? ` (${dup.existing_email})` : ''}
                    </p>
                    {dup.uploaded_at && (
                      <p className="text-xs text-gray-400">Uploaded: {new Date(dup.uploaded_at).toLocaleDateString()}</p>
                    )}
                  </div>
                  <button
                    onClick={() => viewOriginalResume(dup.existing_doc_id)}
                    className="text-xs px-2 py-1 rounded-md border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex-shrink-0 whitespace-nowrap"
                    title="View the existing resume in database"
                  >
                    View
                  </button>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex gap-3 flex-shrink-0">
              <button
                onClick={() => handleDuplicateDecision('skip')}
                disabled={isProcessingDuplicates}
                className="flex-1 py-2.5 px-4 rounded-xl text-sm font-medium border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all disabled:opacity-50"
              >
                Skip {selectedCount > 0 ? `(${selectedCount})` : 'All'}
              </button>
              <button
                onClick={() => handleDuplicateDecision('reparse')}
                disabled={isProcessingDuplicates || selectedCount === 0}
                className="flex-1 py-2.5 px-4 rounded-xl text-sm font-medium text-white transition-all disabled:opacity-50"
                style={{ background: `linear-gradient(135deg, ${primaryColor}, ${accentColor})` }}
              >
                {isProcessingDuplicates ? 'Processing...' : `Re-parse${selectedCount > 0 ? ` (${selectedCount})` : ''}`}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ─────── Parsing Stats Modal ─────── */}
      {showStatsModal && parsingStats && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={closeStatsModal}>
          <div
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden m-4 transform transition-all"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 text-center">
              <div className="flex justify-center mb-3">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: `${secondaryColor}15` }}>
                  <svg className="w-8 h-8" style={{ color: secondaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Resume Processing Complete!</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Successfully processed {parsingStats.progress?.total || 0} resume(s)
              </p>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="flex items-center justify-between p-4 rounded-xl" style={{ backgroundColor: `${secondaryColor}08` }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${secondaryColor}15` }}>
                    <svg className="w-5 h-5" style={{ color: secondaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">New Resumes</p>
                    <p className="text-xs text-gray-500 dark:text-gray-500">Added to database</p>
                  </div>
                </div>
                <span className="text-2xl font-bold" style={{ color: secondaryColor }}>{parsingStats.progress?.new_resumes || 0}</span>
              </div>
              <div className="flex items-center justify-between p-4 rounded-xl" style={{ backgroundColor: `${primaryColor}08` }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${primaryColor}15` }}>
                    <svg className="w-5 h-5" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Updated Resumes</p>
                    <p className="text-xs text-gray-500 dark:text-gray-500">Existing records refreshed</p>
                  </div>
                </div>
                <span className="text-2xl font-bold" style={{ color: primaryColor }}>{parsingStats.progress?.duplicates_updated || 0}</span>
              </div>
              {(parsingStats.progress?.failed || 0) > 0 && (
                <div className="flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/20 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
                      <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Failed</p>
                      <p className="text-xs text-gray-500 dark:text-gray-500">Could not process</p>
                    </div>
                  </div>
                  <span className="text-2xl font-bold text-red-600 dark:text-red-400">{parsingStats.progress?.failed || 0}</span>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <button
                onClick={closeStatsModal}
                className="w-full py-3 rounded-xl text-white font-medium transition-colors"
                style={{ background: `linear-gradient(135deg, ${primaryColor}, ${accentColor})` }}
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─────── PDF Viewer Modal ─────── */}
      {pdfViewerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={closePdfViewer}>
          <div
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-5xl h-[90vh] overflow-hidden m-4 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${primaryColor}15` }}>
                  <svg className="w-5 h-5" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Resume Document</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{previewResume?.name || 'Candidate'}</p>
                </div>
              </div>
              <button onClick={closePdfViewer} className="p-2 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 bg-gray-100 dark:bg-gray-800 overflow-hidden">
              {isPdfLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <svg className="w-10 h-10 mx-auto animate-spin" style={{ color: primaryColor }} fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <p className="mt-3 text-gray-500 dark:text-gray-400">Loading resume...</p>
                  </div>
                </div>
              ) : pdfBlobUrl ? (
                <iframe src={pdfBlobUrl} className="w-full h-full border-0" title="Resume PDF Viewer" />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-500 dark:text-gray-400">Failed to load resume</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─────── Main Content ─────── */}
      <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 w-full">
        <div className="w-full">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center gap-3">
                <button
                  onClick={handleBackNavigation}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-500 dark:text-gray-400"
                  title={openedFromDashboard ? 'Back to dashboard' : 'Go back'}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                Resume Database
              </h1>
              <p className="text-gray-500 dark:text-gray-400 mt-1">Upload, manage, and analyze your candidate resumes</p>
            </div>

            {!(healthChecked && !aiHealthy) && (
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {openedFromDashboard && (
                <button
                  onClick={handleBackNavigation}
                  className="px-4 py-2 rounded-full text-sm font-medium transition-colors text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100"
                >
                  Back to Dashboard
                </button>
              )}
              {/* Progress Bar — visible in all tabs */}
              {userData.userId && userData.cid && (
                <ResumeProgressBar
                  userId={userData.userId}
                  cid={userData.cid}
                  onComplete={handleProgressComplete}
                  onProgressUpdate={handleProgressUpdate}
                  triggerCheck={progressTrigger}
                />
              )}

              {/* Total Resumes Badge */}
              {/* <div
                className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium border"
                style={{
                  backgroundColor: `${secondaryColor}10`,
                  borderColor: `${secondaryColor}30`,
                  color: secondaryColor,
                }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>{resumeStats.total}</span>
              </div> */}

              {/* View Toggle - 3 modes: Upload, Analytics, Database */}
              <div className="flex items-center gap-1 bg-gray-200 dark:bg-gray-800 rounded-lg p-1">
                {[
                  { key: 'upload', label: 'Upload', icon: (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                  )},
                  { key: 'analytics', label: 'Analytics', icon: (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  )},
                  { key: 'database', label: 'Database', icon: (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                    </svg>
                  )},
                ].filter(({ key }) => key !== 'upload' || canUseFeature('resume_upload')).map(({ key, label, icon }) => (
                  <button
                    key={key}
                    onClick={() => setViewMode(key)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                      viewMode === key
                        ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    <span className="flex items-center gap-2">{icon}{label}</span>
                  </button>
                ))}
              </div>
            </div>
            )}
          </div>

          {/* AI Service Unavailable */}
          {viewMode === 'upload' && !canUseFeature('resume_upload') ? (
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 p-16 text-center mt-2">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Resume upload disabled</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 max-w-lg mx-auto">
                {getFeatureBlockedMessage('resume_upload')}
              </p>
            </div>
          ) : (
          <>
          {healthChecked && !aiHealthy ? (
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 p-16 text-center mt-2">
              <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 bg-red-50 dark:bg-red-900/20">
                <svg className="w-10 h-10 text-red-500 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Resume Parsing Service Unavailable</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 max-w-lg mx-auto">
                The resume parsing service is not responding. Upload, analytics, and database features are temporarily unavailable. Please try again later.
              </p>
            </div>
          ) : (
          <>
          {/* AI Quota Warning — uploads still work, parsing delayed */}
          {aiQuotaExceeded && (
            <div className="mb-4 p-4 rounded-xl border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 flex items-start gap-3">
              <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-red-800 dark:text-red-300">Resume AI Service Unavailable</p>
                <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">AI service quota has been exceeded. Resume uploads are temporarily disabled. Please check your AI provider billing or wait for the quota to reset.</p>
              </div>
            </div>
          )}
          </>
          )}
          {/* ═══════════ UPLOAD VIEW ═══════════ */}
          {viewMode === 'upload' && (
            <div className="w-full">
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 p-8">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Upload Resumes</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Upload resumes (PDF, DOC, DOCX) to automatically extract candidate information
                  </p>
                </div>

                <div
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  className="border-2 border-dashed rounded-xl p-12 text-center transition-all hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  style={{
                    borderColor: resumeFiles.length > 0 ? `${accentColor}60` : isDark ? '#374151' : '#D1D5DB',
                    backgroundColor: resumeFiles.length > 0 ? `${accentColor}05` : 'transparent',
                  }}
                >
                  <input ref={fileInputRef} type="file" multiple accept=".pdf,.doc,.docx" onChange={handleFileInputChange} className="hidden" />
                  <input ref={folderInputRef} type="file" webkitdirectory="" directory="" multiple onChange={handleFolderInputChange} className="hidden" />
                  <div
                    className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${accentColor}15` }}
                  >
                    <svg className="w-8 h-8" style={{ color: accentColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 font-medium mb-1">Drag & drop files or folders here</p>
                  <p className="text-sm text-gray-500 dark:text-gray-500 mb-3">PDF, DOC, DOCX supported</p>
                  <div className="flex items-center justify-center gap-3">
                    <button
                      type="button"
                      disabled={isUploading || isParsingActive}
                      onClick={(e) => { e.stopPropagation(); if (!isUploading && !isParsingActive) fileInputRef.current?.click(); }}
                      className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                        isUploading || isParsingActive
                          ? 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50'
                          : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      Select Files
                    </button>
                    <button
                      type="button"
                      disabled={isUploading || isParsingActive}
                      onClick={(e) => { e.stopPropagation(); if (!isUploading && !isParsingActive) folderInputRef.current?.click(); }}
                      className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                        isUploading || isParsingActive
                          ? 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50'
                          : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      <span className="flex items-center gap-1.5">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                        </svg>
                        Select Folder
                      </span>
                    </button>
                  </div>
                </div>

                {resumeFiles.length > 0 && (
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Selected Files ({resumeFiles.length})</h3>
                      <button onClick={() => { setResumeFiles([]); showNotification('All selected files cleared.', 'info'); }} className="text-sm text-red-500 hover:text-red-600 transition-colors">Clear all</button>
                    </div>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {resumeFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-xl group">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-8 h-8 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                              <svg className="w-4 h-4 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">{file.name}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                            </div>
                          </div>
                          <button onClick={() => removeFile(index)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors flex-shrink-0" title="Remove file">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={handleUpload}
                  disabled={isUploading || isParsingActive || resumeFiles.length === 0 || aiQuotaExceeded}
                  className={`w-full mt-6 py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                    isUploading || isParsingActive || resumeFiles.length === 0 || aiQuotaExceeded
                      ? 'bg-gray-200 dark:bg-gray-800 text-gray-500 dark:text-gray-500 cursor-not-allowed'
                      : 'text-white shadow-lg hover:shadow-xl'
                  }`}
                  style={!(isUploading || isParsingActive || resumeFiles.length === 0 || aiQuotaExceeded) ? {
                    background: `linear-gradient(135deg, ${primaryColor}, ${accentColor})`,
                  } : {}}
                >
                  {isParsingActive ? (
                    <>
                      <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Resume parsing in progress...
                    </>
                  ) : isUploading ? (
                    <>
                      <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {uploadProgress.total > 0
                        ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...`
                        : 'Processing...'}
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      Upload {resumeFiles.length > 0 ? `${resumeFiles.length} Resume(s)` : 'Resumes'}
                    </>
                  )}
                </button>

                {/* Retry Failed — shown when there are queued retries */}
                {retryCount > 0 && (
                  <div className="mt-3 p-4 rounded-xl border-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20">
                    <p className="text-sm text-amber-700 dark:text-amber-300 mb-2">
                      {retryCount} resume(s) couldn't be processed due to API rate limits and are queued for retry.
                    </p>
                  <button
                    onClick={handleRetryFailed}
                    disabled={isRetrying}
                    className="w-full py-2.5 px-4 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2 border border-amber-400 dark:border-amber-600 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40"
                  >
                    {isRetrying ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Retrying...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Retry {retryCount} Failed Resume(s)
                      </>
                    )}
                  </button>
                  </div>
                )}

                {/* Upload Tips */}
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { title: 'Supported Formats', desc: 'PDF, DOC, DOCX', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
                    { title: 'Auto Extraction', desc: 'Skills, experience & roles', icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' },
                    { title: 'Batch Upload', desc: `Up to ${UPLOAD_CONFIG.SOFT_FILE_LIMIT} recommended`, icon: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12' },
                  ].map((tip, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${primaryColor}10` }}>
                        <svg className="w-4 h-4" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tip.icon} />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">{tip.title}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-500">{tip.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Upload Log — only in Upload tab */}
              {userData.userId && userData.cid && (
                <ResumeUploadLog
                  userId={userData.userId}
                  cid={userData.cid}
                  fileLogs={uploadFileLogs}
                  isProcessing={isParsingActive}
                />
              )}
            </div>
          )}

          {/* ═══════════ ANALYTICS VIEW ═══════════ */}
          {viewMode === 'analytics' && (
            <div className="space-y-6">
              {/* Stats Cards Row - Clickable to switch charts */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                  label="Total Candidates"
                  value={analyticsStats?.total || resumeStats.total}
                  isLoading={analyticsLoading}
                  gradient={activeChartType === 'role'}
                  color={primaryColor}
                  isActive={activeChartType === 'role'}
                  onClick={() => handleStatCardClick('role')}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  }
                />
                <StatCard
                  label="Experience Levels"
                  value={(analyticsStats?.byExperience || resumeStats.byExperience).length}
                  isLoading={analyticsLoading}
                  gradient={activeChartType === 'experience'}
                  color={secondaryColor}
                  isActive={activeChartType === 'experience'}
                  onClick={() => handleStatCardClick('experience')}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  }
                />
                <StatCard
                  label="Industries"
                  value={(analyticsStats?.byIndustry || resumeStats.byIndustry).length}
                  isLoading={analyticsLoading}
                  gradient={activeChartType === 'industry'}
                  color={accentColor}
                  isActive={activeChartType === 'industry'}
                  onClick={() => handleStatCardClick('industry')}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  }
                />
                <StatCard
                  label="Locations"
                  value={(analyticsStats?.byLocation || resumeStats.byLocation || []).length}
                  isLoading={analyticsLoading}
                  gradient={activeChartType === 'location'}
                  color="#F06595"
                  isActive={activeChartType === 'location'}
                  onClick={() => handleStatCardClick('location')}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  }
                />
              </div>

              {/* Charts Section - No extra tabs, driven by StatCard clicks */}
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 overflow-hidden">
                <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {activeChartType === 'role' && 'Role Distribution'}
                      {activeChartType === 'experience' && 'Experience Level Distribution'}
                      {activeChartType === 'industry' && 'Industry Distribution'}
                      {activeChartType === 'location' && 'Location Distribution'}
                    </h3>
                    <span className="text-xs text-gray-400 dark:text-gray-500 hidden sm:inline">Click a stat card to switch</span>
                  </div>
                  <button
                    onClick={() => { setAnalyticsStats(null); fetchAnalyticsStats(); }}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                  </button>
                </div>

                {analyticsLoading ? (
                  <div className="p-12 text-center">
                    <svg className="w-10 h-10 mx-auto animate-spin" style={{ color: primaryColor }} fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <p className="mt-3 text-gray-500 dark:text-gray-400">Loading analytics...</p>
                  </div>
                ) : (
                  <div key={chartAnimKey} className="p-6">
                    {activeChartType === 'role' && (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                        <DonutChart
                          data={analyticsStats?.byRole || resumeStats.byRole}
                          title="Candidates by Role"
                          size={220}
                          maxItems={8}
                        />
                        <div className="max-h-[420px] overflow-y-auto pr-1 custom-chart-scroll">
                          <HorizontalBarChart
                            data={analyticsStats?.byRole || resumeStats.byRole}
                            title="Top Roles"
                            maxItems={20}
                            colorMode="gradient"
                          />
                        </div>
                      </div>
                    )}
                    {activeChartType === 'experience' && (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                        <DonutChart
                          data={analyticsStats?.byExperience || resumeStats.byExperience}
                          title="Candidates by Experience Level"
                          size={220}
                          maxItems={8}
                        />
                        <div className="max-h-[420px] overflow-y-auto pr-1 custom-chart-scroll">
                          <HorizontalBarChart
                            data={analyticsStats?.byExperience || resumeStats.byExperience}
                            title="Experience Levels"
                            maxItems={20}
                            colorMode="multi"
                          />
                        </div>
                      </div>
                    )}
                    {activeChartType === 'industry' && (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                        <DonutChart
                          data={analyticsStats?.byIndustry || resumeStats.byIndustry}
                          title="Candidates by Industry"
                          size={220}
                          maxItems={8}
                        />
                        <div className="max-h-[420px] overflow-y-auto pr-1 custom-chart-scroll">
                          <HorizontalBarChart
                            data={analyticsStats?.byIndustry || resumeStats.byIndustry}
                            title="Top Industries"
                            maxItems={20}
                            colorMode="gradient"
                          />
                        </div>
                      </div>
                    )}
                    {activeChartType === 'location' && (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                        <DonutChart
                          data={analyticsStats?.byLocation || resumeStats.byLocation}
                          title="Candidates by Location"
                          size={220}
                          maxItems={8}
                        />
                        <div className="max-h-[420px] overflow-y-auto pr-1 custom-chart-scroll">
                          <HorizontalBarChart
                            data={analyticsStats?.byLocation || resumeStats.byLocation}
                            title="Top Locations"
                            maxItems={20}
                            colorMode="multi"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Overview - Shows only the selected chart type data with larger elements */}
              {(analyticsStats?.total || totalCount) > 0 && (
                <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 p-6">
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {activeChartType === 'role' && 'Top Roles'}
                        {activeChartType === 'experience' && 'Experience Levels'}
                        {activeChartType === 'industry' && 'Top Industries'}
                        {activeChartType === 'location' && 'Top Locations'}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        Click any item to view matching candidates &middot; Avg Experience: <span className="font-medium" style={{ color: accentColor }}>{avgExperience}</span>
                      </p>
                    </div>
                    <button
                      onClick={() => { setCameFromAnalytics(true); setViewMode('database'); }}
                      className="text-sm font-medium transition-colors flex items-center gap-1"
                      style={{ color: primaryColor }}
                    >
                      View All Resumes
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>

                  <div key={`overview-${activeChartType}`} className="animate-chartFade">
                    {/* Dynamic content based on activeChartType */}
                    {(() => {
                      let items = [];
                      let chipColor = primaryColor;
                      let clickAction = (item) => { setSearchQuery(item.name); setViewMode('database'); };

                      if (activeChartType === 'role') {
                        items = analyticsStats?.byRole || resumeStats.byRole;
                        chipColor = primaryColor;
                      } else if (activeChartType === 'experience') {
                        items = analyticsStats?.byExperience || resumeStats.byExperience;
                        chipColor = secondaryColor;
                      } else if (activeChartType === 'industry') {
                        items = analyticsStats?.byIndustry || resumeStats.byIndustry;
                        chipColor = accentColor;
                      } else if (activeChartType === 'location') {
                        items = analyticsStats?.byLocation || resumeStats.byLocation;
                        chipColor = '#F06595';
                      }

                      // All category clicks: fetch matching resumes immediately from backend
                      clickAction = (item) => {
                        // Cancel any pending debounced search and skip the next one
                        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
                        skipNextDebounceRef.current = true;
                        setSearchQuery(item.name);
                        setCurrentPage(1);
                        // Fetch immediately — don't wait for debounce
                        fetchResumeList(1, item.name);
                        setCameFromAnalytics(true);
                        setViewMode('database');
                      };

                      if (!items || items.length === 0) {
                        return (
                          <div className="text-center py-8">
                            <p className="text-gray-500 dark:text-gray-400 text-sm">No data available for this category</p>
                          </div>
                        );
                      }

                      const INITIAL_SHOW = 12;
                      return (
                        <div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {items.slice(0, showAllCategories ? undefined : INITIAL_SHOW).map((item, idx) => (
                            <button
                              key={idx}
                              onClick={() => clickAction(item)}
                              className="flex items-center justify-between gap-3 px-5 py-3.5 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-md hover:scale-[1.01] transition-all text-left group"
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <div
                                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                  style={{ backgroundColor: chipColor }}
                                />
                                <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{item.name}</span>
                              </div>
                              <span
                                className="text-sm px-2.5 py-1 rounded-lg font-bold flex-shrink-0 transition-colors"
                                style={{ backgroundColor: `${chipColor}12`, color: chipColor }}
                              >
                                {item.count}
                              </span>
                            </button>
                          ))}
                        </div>
                        {items.length > INITIAL_SHOW && (
                          <div className="mt-3 text-center">
                            <button
                              onClick={() => setShowAllCategories(prev => !prev)}
                              className="text-sm font-medium transition-colors hover:opacity-80"
                              style={{ color: chipColor }}
                            >
                              {showAllCategories ? 'Show less' : `Show all ${items.length} items`}
                            </button>
                          </div>
                        )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* CSS Animations */}
              <style>{`
                @keyframes fadeIn {
                  from { opacity: 0; transform: translateY(16px); }
                  to { opacity: 1; transform: translateY(0); }
                }
                @keyframes chartFade {
                  0% { opacity: 0; transform: scale(0.92) translateY(8px); }
                  60% { opacity: 1; transform: scale(1.01) translateY(-2px); }
                  100% { opacity: 1; transform: scale(1) translateY(0); }
                }
                @keyframes slideUp {
                  from { opacity: 0; transform: translateY(20px); }
                  to { opacity: 1; transform: translateY(0); }
                }
                .animate-fadeIn { animation: fadeIn 0.5s ease-out both; }
                .animate-chartFade { animation: chartFade 0.5s ease-out both; }
                .animate-slideUp { animation: slideUp 0.4s ease-out both; }
                .custom-chart-scroll::-webkit-scrollbar { width: 5px; }
                .custom-chart-scroll::-webkit-scrollbar-track { background: transparent; }
                .custom-chart-scroll::-webkit-scrollbar-thumb { background: rgba(156, 163, 175, 0.3); border-radius: 3px; }
                .custom-chart-scroll::-webkit-scrollbar-thumb:hover { background: rgba(156, 163, 175, 0.5); }
              `}</style>
            </div>
          )}

          {/* ═══════════ DATABASE VIEW ═══════════ */}
          {viewMode === 'database' && (
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 overflow-hidden">
              {/* Back to Analytics breadcrumb */}
              {cameFromAnalytics && (
                <div className="px-4 pt-3 pb-0">
                  <button
                    onClick={() => { setCameFromAnalytics(false); setViewMode('analytics'); }}
                    className="inline-flex items-center gap-1.5 text-sm font-medium transition-colors hover:opacity-80"
                    style={{ color: primaryColor }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Analytics
                    {searchQuery && <span className="text-gray-400 dark:text-gray-500 font-normal ml-1">/ Showing results for &ldquo;{searchQuery}&rdquo;</span>}
                  </button>
                </div>
              )}
              {/* Search & Filter Bar */}
              <div className="p-4 border-b border-gray-200 dark:border-gray-800">
                <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
                  {/* Search */}
                  <div className="flex-1 w-full lg:max-w-lg relative">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      placeholder="Search by name, email, role, skills, location..."
                      value={searchQuery}
                      onChange={(e) => { setSearchQuery(e.target.value); setIsSearching(true); }}
                      className="w-full pl-10 pr-10 py-2.5 bg-gray-100 dark:bg-gray-800 border-0 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 transition-all outline-none"
                      style={{ '--tw-ring-color': `${primaryColor}40` }}
                    />
                    {/* Search debounce spinner or clear button */}
                    {isSearching ? (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <svg className="w-4 h-4 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      </div>
                    ) : searchQuery ? (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    ) : null}
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Filters Toggle */}
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className={`inline-flex items-center px-3 py-2.5 border rounded-xl text-sm font-medium transition-colors ${
                        showFilters || hasActiveFilters
                          ? 'border-transparent text-white'
                          : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                      style={(showFilters || hasActiveFilters) ? { backgroundColor: primaryColor } : {}}
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
                      </svg>
                      Filters
                      {hasActiveFilters && (
                        <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold bg-white rounded-full" style={{ color: primaryColor }}>
                          {(filterExperience !== 'all' ? 1 : 0) + (filterIndustry !== 'all' ? 1 : 0) + (filterLocation !== 'all' ? 1 : 0) + (searchQuery ? 1 : 0)}
                        </span>
                      )}
                    </button>

                    {/* Results count */}
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Results:</span>
                      <span className="px-2 py-1 rounded-full text-xs font-medium text-white" style={{ backgroundColor: primaryColor }}>
                        {totalCount}
                      </span>
                    </div>

                    {/* Refresh — also triggers hash backfill for this company */}
                    <button
                      onClick={async () => {
                        if (isLoading) return;
                        setRefreshTrigger(prev => prev + 1);
                        setAnalyticsStats(null);
                        // Trigger company-scoped hash backfill in background
                        if (userData.cid) {
                          try {
                            const result = await coreApi.post('/resume-parsing/refresh-hashes', { cid: userData.cid });
                            if (result.data?.total > 0) {
                              showNotification(`Hash backfill: ${result.data.updated} resume(s) hashed, ${result.data.skipped} skipped.`, 'info');
                            }
                          } catch (_) {
                            // Non-critical — don't show error for backfill
                          }
                        }
                      }}
                      disabled={isLoading}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium border transition-colors ${
                        isLoading
                          ? 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                          : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      <svg className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      {isLoading ? 'Loading...' : 'Refresh'}
                    </button>
                  </div>
                </div>

                {/* Advanced Filters Panel */}
                {showFilters && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    {/* Clear filters at top for visibility */}
                    {hasActiveFilters && (
                      <div className="flex justify-end mb-3">
                        <button
                          onClick={clearAllFilters}
                          className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 border border-red-200 dark:border-red-800 transition-colors"
                        >
                          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          Clear All Filters
                        </button>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {/* Experience Level Dropdown */}
                      <FilterDropdown
                        label="Experience Level"
                        value={filterExperience}
                        onChange={setFilterExperience}
                        options={uniqueExperienceLevels}
                        placeholder="All Levels"
                      />

                      {/* Industry Dropdown */}
                      <FilterDropdown
                        label="Industry"
                        value={filterIndustry}
                        onChange={setFilterIndustry}
                        options={uniqueIndustries}
                        placeholder="All Industries"
                      />

                      {/* Location Dropdown */}
                      <FilterDropdown
                        label="Location"
                        value={filterLocation}
                        onChange={setFilterLocation}
                        options={uniqueLocations}
                        placeholder="All Locations"
                      />

                      {/* Sort By Dropdown */}
                      <FilterDropdown
                        label="Sort By"
                        value={`${sortField}-${sortDirection}`}
                        onChange={(val) => {
                          const [f, d] = val.split('-');
                          setSortField(f);
                          setSortDirection(d);
                        }}
                        options={[
                          { value: 'name-asc', label: 'Name (A-Z)' },
                          { value: 'name-desc', label: 'Name (Z-A)' },
                          { value: 'experience-desc', label: 'Experience (High-Low)' },
                          { value: 'experience-asc', label: 'Experience (Low-High)' },
                          { value: 'date-desc', label: 'Newest First' },
                          { value: 'date-asc', label: 'Oldest First' },
                        ]}
                        placeholder="Name (A-Z)"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Table */}
              {isLoading ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-800/50">
                        {['Candidate', 'Role', 'Exp Level', 'Experience', 'Industry', 'Location', 'Actions'].map((h) => (
                          <th key={h} className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <tr key={i} className="animate-pulse">
                          <td className="px-6 py-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700" /><div><div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-2" /><div className="h-3 w-24 bg-gray-100 dark:bg-gray-800 rounded" /></div></div></td>
                          <td className="px-6 py-4"><div className="h-4 w-28 bg-gray-200 dark:bg-gray-700 rounded" /></td>
                          <td className="px-6 py-4"><div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded-full" /></td>
                          <td className="px-6 py-4"><div className="h-4 w-12 bg-gray-200 dark:bg-gray-700 rounded" /></td>
                          <td className="px-6 py-4"><div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded" /></td>
                          <td className="px-6 py-4"><div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded" /></td>
                          <td className="px-6 py-4 text-center"><div className="h-7 w-16 bg-gray-200 dark:bg-gray-700 rounded-lg mx-auto" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : paginatedResumes.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: `${accentColor}15` }}>
                    <svg className="w-8 h-8" style={{ color: accentColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    {hasActiveFilters ? 'No matching resumes found' : 'No resumes yet'}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    {hasActiveFilters
                      ? `No results for your current search${searchQuery ? ` "${searchQuery}"` : ''} and filters. Try different criteria.`
                      : 'Upload your first batch of resumes to build your candidate database.'}
                  </p>
                  <div className="mt-4 flex items-center justify-center gap-3">
                    {hasActiveFilters && (
                      <button onClick={clearAllFilters} className="px-4 py-2 rounded-lg font-medium transition-colors border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">
                        Clear All Filters
                      </button>
                    )}
                    <button onClick={() => setViewMode('upload')} className="px-4 py-2 rounded-lg text-white font-medium transition-colors" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${accentColor})` }}>
                      Upload Resumes
                    </button>
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-800/50">
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" onClick={() => handleSort('name')}>
                          <div className="flex items-center">Candidate{getSortIcon('name')}</div>
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" onClick={() => handleSort('role')}>
                          <div className="flex items-center">Role{getSortIcon('role')}</div>
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          <div className="flex items-center">Experience Level</div>
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" onClick={() => handleSort('experience')}>
                          <div className="flex items-center">Experience{getSortIcon('experience')}</div>
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">
                          <div className="flex items-center">Industry</div>
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">
                          <div className="flex items-center">Location</div>
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {paginatedResumes.map((resume, index) => (
                        <tr
                          key={resume.doc_id || index}
                          onClick={() => openResumePreview(resume)}
                          className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
                        >
                          {/* Name */}
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div
                                className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
                                style={{ background: `linear-gradient(135deg, ${primaryColor}, ${accentColor})` }}
                              >
                                {(resume.name || 'C').charAt(0).toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-gray-900 dark:text-white truncate">{resume.name || 'Unknown'}</p>
                                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{resume.email || '-'}</p>
                              </div>
                            </div>
                          </td>
                          {/* Role */}
                          <td className="px-6 py-4">
                            <div className="min-w-0">
                              <p className="text-gray-900 dark:text-white font-medium truncate max-w-[200px]">
                                {resume.potential_roles?.split(',')[0]?.trim() || '-'}
                              </p>
                              {resume.core_skills && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {resume.core_skills.split(',').slice(0, 2).map((skill, idx) => (
                                    <span
                                      key={idx}
                                      className="text-xs px-2 py-0.5 rounded-full"
                                      style={{ backgroundColor: `${primaryColor}12`, color: primaryColor }}
                                    >
                                      {skill.trim()}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                          {/* Experience Level */}
                          <td className="px-6 py-4">
                            {resume.experience_level ? (
                              <span
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                style={{ backgroundColor: `${secondaryColor}15`, color: secondaryColor }}
                              >
                                {resume.experience_level}
                              </span>
                            ) : (
                              <span className="text-gray-400 dark:text-gray-600">-</span>
                            )}
                          </td>
                          {/* Experience Duration */}
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                              {formatExperience(resume.total_experience_in_months)}
                            </span>
                          </td>
                          {/* Industry — hidden on mobile */}
                          <td className="px-6 py-4 hidden lg:table-cell">
                            <p className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-[150px]">
                              {resume.industry_backgrounds?.split(',')[0]?.trim() || '-'}
                            </p>
                          </td>
                          {/* Location — hidden on mobile */}
                          <td className="px-6 py-4 hidden lg:table-cell">
                            <p className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-[120px]">
                              {resume.location || '-'}
                            </p>
                          </td>
                          {/* Actions */}
                          <td className="px-6 py-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); openResumePreview(resume); }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all hover:shadow-md"
                                style={{ backgroundColor: `${primaryColor}12`, color: primaryColor }}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                View
                              </button>
                              {resume.email && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); window.open(`mailto:${resume.email}`, '_blank'); }}
                                  className="p-2 rounded-lg transition-colors"
                                  style={{ color: primaryColor }}
                                  title="Contact via Email"
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${primaryColor}10`}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {totalCount > 0 && (
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/30 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} resumes
                    {searchQuery && ` matching "${searchQuery}"`}
                  </p>

                  {totalPages > 1 && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === 1
                            ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
                        }`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>

                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          return (
                            <button
                              key={pageNum}
                              onClick={() => goToPage(pageNum)}
                              className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                                currentPage === pageNum
                                  ? 'text-white'
                                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
                              }`}
                              style={currentPage === pageNum ? { background: `linear-gradient(135deg, ${primaryColor}, ${accentColor})` } : {}}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === totalPages
                            ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
                        }`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              )}
              </div>
          )}
          </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ResumeParsingPage;
